import React, {
  useMemo,
  useState,
} from "react"

import {
  MinusIcon,
  PlusIcon,
} from "@heroicons/react/24/outline"

import {
  UserGroupIcon,
} from "@heroicons/react/24/solid"

import {
  userProfileIcon2,
} from "@/assets/img/imgAssets"

import {
  useGetUserPicQuery,
} from "../users/usersApiSlice"

import {
  useLazyGetUserChallengeTeamEvalsByContextQuery,
} from "./userChallengeTeamEvalsApiSlice"

import EtModalAddOrUpdateUserChallengeTeamEval from "@/components/common/modal/EtModalAddOrUpdateUserChallengeTeamEval"


const EtUserSessionTeamChallengeEvaluations = ({
  sessionChallenges = [],

  teamEvaluatorId,
  teamEvaluatorOrgId,

  trailId,
  sessionId,
  cohortId,
  cohort,

  allOrgUsers = [],
}) => {

  // First Created - 20 Aug 2026
  // Author - Atul Pandey
  // Purpose - Allow a user to evaluate eligible peers within the same cohort
  // Updated - 20 Aug 2026 - Multiple Team evaluation support
  // Updated - 20 Aug 2026 - Team evaluator eligibility
  // Updated - 20 Aug 2026 - Context based Team evaluation reads


  // 1 - Normalize populated and non populated Mongo references
  const getIdValue = (
    value
  ) => {
    return (
      value?._id?.toString() ||
      value?.toString() ||
      ""
    )
  }


  // 2 - Section display state
  const [
    showTeamEvaluations,
    setShowTeamEvaluations,
  ] = useState(false)

  const [
    showChallengeSections,
    setShowChallengeSections,
  ] = useState({})


  // 3 - Selected peer / challenge state
  const [
    selectedChallengeId,
    setSelectedChallengeId,
  ] = useState(null)

  const [
    selectedPeer,
    setSelectedPeer,
  ] = useState(null)

  const [
    selectedPeerEvaluations,
    setSelectedPeerEvaluations,
  ] = useState([])


  // 4 - Modal state
  const [
    isModalOpen,
    setIsModalOpen,
  ] = useState(false)

  const [
    triggeringUrl,
    setTriggeringUrl,
  ] = useState(null)

  const [
    mode,
    setMode,
  ] = useState("create")

  const [
    existingEvalData,
    setExistingEvalData,
  ] = useState(null)


  // 5 - Context based Team evaluation query
  const [
    getUserChallengeTeamEvalsByContext,
    {
      isFetching:
        isFetchingTeamEvaluations,
      isError:
        isTeamEvaluationsError,
      error:
        teamEvaluationsError,
    },
  ] =
    useLazyGetUserChallengeTeamEvalsByContextQuery()


  // 6 - Find the current Team evaluator's User record
  const teamEvaluator =
    useMemo(() => {
      return allOrgUsers.find(
        (user) =>
          getIdValue(
            user?._id
          ) ===
          getIdValue(
            teamEvaluatorId
          )
      )
    }, [
      allOrgUsers,
      teamEvaluatorId,
    ])


  // 7 - Determine whether the current user
  // is eligible to act as a Team evaluator
  const evaluatorHasCoachRole =
    teamEvaluator
      ?.roles
      ?.some(
        (role) =>
          [
            "EtCoach",
            "OrgCoach",
            "Coach",
          ].includes(role)
      ) || false


  const evaluatorIsOrgSponsor =
    teamEvaluator
      ?.roles
      ?.includes(
        "OrgSponsor"
      ) || false


  const evaluatorIsCohortCoach =
    cohort
      ?.coachIds
      ?.some(
        (coach) =>
          getIdValue(
            coach?.coachId
          ) ===
          getIdValue(
            teamEvaluatorId
          )
      ) || false


  const canEvaluateTeam =
    Boolean(
      teamEvaluatorId &&
      teamEvaluatorOrgId &&
      cohortId
    ) &&
    !evaluatorHasCoachRole &&
    !evaluatorIsOrgSponsor &&
    !evaluatorIsCohortCoach


  // 8 - Build the eligible peer list from
  // the authoritative cohort membership record
  const eligiblePeers =
    useMemo(() => {

      if (
        !Array.isArray(
          cohort?.cohortUserIds
        )
      ) {
        return []
      }

      const normalizedEvaluatorId =
        getIdValue(
          teamEvaluatorId
        )

      const seenUserIds =
        new Set()

      return cohort
        .cohortUserIds
        .filter(
          (cohortUser) => {

            const cohortUserId =
              getIdValue(
                cohortUser?.userId
              )

            if (
              !cohortUserId ||
              cohortUserId ===
                normalizedEvaluatorId ||
              cohortUser?.active === false ||
              seenUserIds.has(
                cohortUserId
              )
            ) {
              return false
            }

            if (
              [
                "Dropped",
                "Archived",
                "Transferred",
              ].includes(
                cohortUser
                  ?.userCohortStatus
              )
            ) {
              return false
            }

            seenUserIds.add(
              cohortUserId
            )

            return true
          }
        )
        .map(
          (cohortUser) => {

            const cohortUserId =
              getIdValue(
                cohortUser?.userId
              )

            const userData =
              allOrgUsers.find(
                (user) =>
                  getIdValue(
                    user?._id
                  ) ===
                  cohortUserId
              )

            return userData || (
              typeof cohortUser
                ?.userId ===
                "object"
                ? cohortUser.userId
                : {
                    _id:
                      cohortUserId,
                  }
            )
          }
        )
        .filter(Boolean)

    }, [
      cohort,
      allOrgUsers,
      teamEvaluatorId,
    ])


  // 9 - Extract normalized data from
  // RTK entity adapter query response
  const extractTeamEvaluations =
    (
      adapterData
    ) => {

      if (
        !adapterData?.ids ||
        !adapterData?.entities
      ) {
        return []
      }

      return adapterData
        .ids
        .map(
          (id) =>
            adapterData
              .entities[id]
        )
        .filter(Boolean)
        .sort(
          (
            evaluationA,
            evaluationB
          ) =>
            Number(
              evaluationB
                ?.evaluationNumber ||
              0
            ) -
            Number(
              evaluationA
                ?.evaluationNumber ||
              0
            )
        )
    }


  // 10 - Load one evaluator → peer → challenge history
  const loadPeerEvaluationHistory =
    async ({
      challengeId,
      peer,
    }) => {

      const peerId =
        getIdValue(
          peer?._id
        )

      const peerOrgId =
        getIdValue(
          peer?.userOrgId
        ) ||
        getIdValue(
          teamEvaluatorOrgId
        )

      if (
        !challengeId ||
        !peerId ||
        !peerOrgId ||
        !cohortId
      ) {
        return
      }

      try {

        const result =
          await getUserChallengeTeamEvalsByContext(
            {
              teamEvaluatorId:
                getIdValue(
                  teamEvaluatorId
                ),

              userId:
                peerId,

              userOrgId:
                peerOrgId,

              trailId:
                getIdValue(
                  trailId
                ),

              sessionId:
                getIdValue(
                  sessionId
                ),

              cohortId:
                getIdValue(
                  cohortId
                ),

              challengeId:
                getIdValue(
                  challengeId
                ),
            }
          ).unwrap()


        setSelectedChallengeId(
          getIdValue(
            challengeId
          )
        )

        setSelectedPeer(
          peer
        )

        setSelectedPeerEvaluations(
          extractTeamEvaluations(
            result
          )
        )

      } catch (error) {

        console.error(
          "Failed to load Team evaluation history:",
          error
        )

        setSelectedPeerEvaluations(
          []
        )
      }
    }


  // 11 - Handle peer selection
  const handleSelectPeer =
    async ({
      challengeId,
      peer,
    }) => {

      const isSameSelection =
        getIdValue(
          selectedChallengeId
        ) ===
          getIdValue(
            challengeId
          ) &&
        getIdValue(
          selectedPeer?._id
        ) ===
          getIdValue(
            peer?._id
          )

      if (
        isSameSelection
      ) {
        setSelectedPeer(
          null
        )

        setSelectedChallengeId(
          null
        )

        setSelectedPeerEvaluations(
          []
        )

        return
      }

      await loadPeerEvaluationHistory({
        challengeId,
        peer,
      })
    }


  // 12 - Open a new Team evaluation
  const handleCreateEvaluation =
    ({
      challengeId,
      peer,
    }) => {

      setTriggeringUrl(
        window.location.pathname
      )

      setSelectedChallengeId(
        getIdValue(
          challengeId
        )
      )

      setSelectedPeer(
        peer
      )

      setMode(
        "create"
      )

      setExistingEvalData(
        null
      )

      setIsModalOpen(
        true
      )
    }


  // 13 - Open one exact existing Team evaluation
  const handleEditEvaluation =
    ({
      challengeId,
      peer,
      evaluation,
    }) => {

      setTriggeringUrl(
        window.location.pathname
      )

      setSelectedChallengeId(
        getIdValue(
          challengeId
        )
      )

      setSelectedPeer(
        peer
      )

      setMode(
        "update"
      )

      setExistingEvalData(
        evaluation
      )

      setIsModalOpen(
        true
      )
    }


  // 14 - Close the modal and refresh
  // the currently selected peer history
  const handleCloseTeamEvaluationModal =
    async () => {

      const challengeIdToRefresh =
        selectedChallengeId

      const peerToRefresh =
        selectedPeer

      setIsModalOpen(
        false
      )

      setTriggeringUrl(
        null
      )

      setExistingEvalData(
        null
      )

      setMode(
        "create"
      )

      if (
        challengeIdToRefresh &&
        peerToRefresh
      ) {
        await loadPeerEvaluationHistory({
          challengeId:
            challengeIdToRefresh,

          peer:
            peerToRefresh,
        })
      }
    }


  // 15 - Challenge display toggle
  const toggleChallenge =
    (
      challengeId
    ) => {

      const challengeKey =
        getIdValue(
          challengeId
        )

      setShowChallengeSections(
        (previous) => ({
          ...previous,

          [challengeKey]:
            !previous[
              challengeKey
            ],
        })
      )
    }


  // 16 - No challenges
  if (
    !sessionChallenges?.length
  ) {
    return null
  }


  // 17 - Only valid Team evaluators
  // should see the Team evaluation section.
  if (
    !canEvaluateTeam
  ) {
    return null
  }


  return (
    <div className="m-1 rounded bg-gray-100 p-1">

      {/* 18 - Team Evaluation Section Header */}
      <div className="m-1 flex items-center justify-between p-1">

        <div className="flex min-w-0 items-center">

          <button
            type="button"
            onClick={() =>
              setShowTeamEvaluations(
                (previous) =>
                  !previous
              )
            }
            className="mx-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full hover:bg-white"
            aria-label={
              showTeamEvaluations
                ? "Collapse Team evaluations"
                : "Expand Team evaluations"
            }
          >
            {showTeamEvaluations ? (
              <MinusIcon className="h-5 w-5 text-orange-600" />
            ) : (
              <PlusIcon className="h-5 w-5 text-orange-600" />
            )}
          </button>


          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200">
            <UserGroupIcon className="h-5 w-5 text-orange-600" />
          </div>


          <div className="min-w-0">

            <p className="mx-2 truncate text-md font-bold text-gray-600">
              Team Evaluations
            </p>

            <p className="mx-2 truncate text-[10px] italic text-gray-600">
              Evaluate members of your cohort by session challenge
            </p>

          </div>

        </div>

      </div>


      {showTeamEvaluations && (
        <div className="m-1 space-y-2 p-1">

          <div className="mx-1 rounded bg-white p-2">

            <p className="text-[10px] text-gray-500">
              Select a challenge and team member to create or review your evaluations.
            </p>

            <p className="mt-1 text-[9px] text-gray-400">
              {eligiblePeers.length} eligible team member
              {eligiblePeers.length === 1
                ? ""
                : "s"}
            </p>

          </div>


          {/* 19 - Session Challenges */}
          {sessionChallenges.map(
            (
              challengeReference,
              challengeIndex
            ) => {

              const challenge =
                challengeReference
                  ?.challengeId ||
                challengeReference

              const challengeId =
                getIdValue(
                  challenge?._id
                )

              const challengeIsOpen =
                showChallengeSections[
                  challengeId
                ]

              const selectedPeerId =
                getIdValue(
                  selectedPeer?._id
                )

              const isSelectedChallenge =
                getIdValue(
                  selectedChallengeId
                ) ===
                challengeId


              return (
                <div
                  key={
                    challengeId ||
                    challengeIndex
                  }
                  className="rounded bg-gray-200 p-1"
                >

                  {/* 20 - Challenge Header */}
                  <div className="mx-1 flex items-start px-1">

                    <button
                      type="button"
                      onClick={() =>
                        toggleChallenge(
                          challengeId
                        )
                      }
                      className="m-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-white"
                    >
                      {challengeIsOpen ? (
                        <MinusIcon className="h-4 w-4 text-orange-600" />
                      ) : (
                        <PlusIcon className="h-4 w-4 text-orange-600" />
                      )}
                    </button>


                    <div className="m-1 min-w-0 p-1">

                      <p className="font-bold">
                        {
                          challenge
                            ?.challengeName
                        }
                      </p>

                      <p className="text-[10px] italic text-gray-500">
                        {
                          challenge
                            ?.challengeDesc
                        }
                      </p>

                    </div>

                  </div>


                  {challengeIsOpen && (
                    <div className="m-1 rounded bg-gray-50 p-2">

                      {/* 21 - Eligible Peers */}
                      <div className="mb-2">

                        <p className="mb-2 text-[10px] font-bold text-gray-500">
                          Team Members
                        </p>


                        {eligiblePeers.length >
                        0 ? (

                          <div className="flex flex-wrap gap-2">

                            {eligiblePeers.map(
                              (
                                peer,
                                peerIndex
                              ) => {

                                const peerId =
                                  getIdValue(
                                    peer?._id
                                  )

                                const isSelected =
                                  isSelectedChallenge &&
                                  selectedPeerId ===
                                    peerId


                                return (
                                  <button
                                    key={
                                      peerId ||
                                      peerIndex
                                    }
                                    type="button"
                                    onClick={() =>
                                      handleSelectPeer({
                                        challengeId,
                                        peer,
                                      })
                                    }
                                    className={`flex min-w-[120px] items-center gap-2 rounded border p-2 text-left transition ${
                                      isSelected
                                        ? "border-orange-400 bg-orange-50"
                                        : "border-gray-200 bg-white hover:border-orange-300"
                                    }`}
                                  >

                                    <TeamEvaluationUser
                                      user={
                                        peer
                                      }
                                    />


                                    <div className="min-w-0">

                                      <p className="truncate text-[10px] font-bold text-gray-700">
                                        {
                                          peer
                                            ?.name ||
                                          "Team Member"
                                        }
                                      </p>

                                      <p className="truncate text-[8px] text-gray-400">
                                        {
                                          peer
                                            ?.username ||
                                          ""
                                        }
                                      </p>

                                    </div>

                                  </button>
                                )
                              }
                            )}

                          </div>

                        ) : (

                          <p className="text-[10px] italic text-gray-400">
                            No eligible team members are available in this cohort.
                          </p>

                        )}

                      </div>


                      {/* 22 - Selected Peer Evaluation History */}
                      {isSelectedChallenge &&
                        selectedPeer && (

                        <div className="mt-3 rounded border border-gray-200 bg-white p-3">

                          <div className="flex flex-wrap items-center justify-between gap-2">

                            <div className="flex items-center gap-2">

                              <TeamEvaluationUser
                                user={
                                  selectedPeer
                                }
                              />

                              <div>

                                <p className="text-xs font-bold text-gray-700">
                                  {
                                    selectedPeer
                                      ?.name ||
                                    "Team Member"
                                  }
                                </p>

                                <p className="text-[9px] text-gray-400">
                                  Your evaluations for this challenge
                                </p>

                              </div>

                            </div>


                            <button
                              type="button"
                              disabled={
                                isFetchingTeamEvaluations
                              }
                              onClick={() =>
                                handleCreateEvaluation({
                                  challengeId,
                                  peer:
                                    selectedPeer,
                                })
                              }
                              className="rounded-full bg-orange-600 px-4 py-1.5 text-[10px] font-semibold text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              New Evaluation
                            </button>

                          </div>


                          {isFetchingTeamEvaluations && (
                            <p className="mt-3 text-[10px] italic text-gray-400">
                              Loading evaluation history...
                            </p>
                          )}


                          {isTeamEvaluationsError && (
                            <p className="mt-3 text-[10px] text-red-600">
                              {
                                teamEvaluationsError
                                  ?.data
                                  ?.message ||
                                "Unable to load Team evaluations."
                              }
                            </p>
                          )}


                          {!isFetchingTeamEvaluations &&
                            !isTeamEvaluationsError &&
                            selectedPeerEvaluations.length ===
                              0 && (

                            <div className="mt-3 rounded bg-gray-50 p-3">

                              <p className="text-[10px] italic text-gray-500">
                                You have not evaluated this team member for this challenge yet.
                              </p>

                            </div>
                          )}


                          {!isFetchingTeamEvaluations &&
                            selectedPeerEvaluations.length >
                              0 && (

                            <div className="mt-3 flex flex-wrap gap-2">

                              {selectedPeerEvaluations.map(
                                (
                                  evaluation
                                ) => (

                                  <div
                                    key={
                                      evaluation
                                        ?._id
                                    }
                                    className="min-w-[155px] rounded border border-gray-200 bg-gray-50 p-3"
                                  >

                                    <div className="flex items-center justify-between gap-2">

                                      <p className="text-[10px] font-bold text-gray-700">
                                        Eval{" "}
                                        {
                                          evaluation
                                            ?.evaluationNumber ||
                                          1
                                        }
                                      </p>


                                      <span className="rounded-full bg-white px-2 py-1 text-[8px] text-gray-500">
                                        {
                                          evaluation
                                            ?.evaluationType ||
                                          "checkpoint"
                                        }
                                      </span>

                                    </div>


                                    <p className="mt-2 text-[9px] text-gray-400">
                                      {
                                        evaluation
                                          ?.userChallengeTeamEvals
                                          ?.length ||
                                        0
                                      }{" "}
                                      dimension
                                      {
                                        evaluation
                                          ?.userChallengeTeamEvals
                                          ?.length ===
                                        1
                                          ? ""
                                          : "s"
                                      }
                                    </p>


                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleEditEvaluation({
                                          challengeId,
                                          peer:
                                            selectedPeer,
                                          evaluation,
                                        })
                                      }
                                      className="mt-3 rounded-full border border-lime-300 bg-lime-50 px-3 py-1 text-[9px] font-semibold text-lime-700 hover:bg-lime-100"
                                    >
                                      Edit
                                    </button>

                                  </div>

                                )
                              )}

                            </div>

                          )}

                        </div>

                      )}

                    </div>
                  )}

                </div>
              )
            }
          )}

        </div>
      )}


      {/* 23 - One Team Evaluation Modal */}
      <EtModalAddOrUpdateUserChallengeTeamEval
        isOpen={
          isModalOpen
        }

        onClose={
          handleCloseTeamEvaluationModal
        }

        mode={
          mode
        }

        existingEvalData={
          existingEvalData
        }

        challengeId={
          selectedChallengeId
        }

        teamEvaluatorId={
          getIdValue(
            teamEvaluatorId
          )
        }

        teamEvaluatorOrgId={
          getIdValue(
            teamEvaluatorOrgId
          )
        }

        userId={
          getIdValue(
            selectedPeer?._id
          )
        }

        userOrgId={
          getIdValue(
            selectedPeer
              ?.userOrgId
          ) ||
          getIdValue(
            teamEvaluatorOrgId
          )
        }

        trailId={
          getIdValue(
            trailId
          )
        }

        sessionId={
          getIdValue(
            sessionId
          )
        }

        cohortId={
          getIdValue(
            cohortId
          )
        }

        triggeringUrl={
          triggeringUrl
        }
      />

    </div>
  )
}


// 24 - Team User Profile
const TeamEvaluationUser = ({
  user,
}) => {

  const username =
    user?.username ||
    ""

  const {
    data: userPic,
    isFetching:
      isFetchingUserPic,
  } =
    useGetUserPicQuery(
      username,
      {
        skip:
          !username,
      }
    )


  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white bg-gradient-to-r from-orange-600 to-orange-400 shadow">

      {isFetchingUserPic ? (

        <div className="text-[7px] text-white">
          ...
        </div>

      ) : userPic ? (

        <img
          src={userPic}
          alt="User"
          className="h-9 w-9 rounded-full"
        />

      ) : (

        <img
          src={
            userProfileIcon2
          }
          alt="User"
          className="h-8 w-8 rounded-full p-1"
        />

      )}

    </div>
  )
}


export default EtUserSessionTeamChallengeEvaluations